import type { ApiResponse, ModelOutput, ManualEvaluation, ModelDataResponse, ModelInputWithOutputs, ModelStats, ModelOutputsResponse, ModelOutputWithInput, ModelRunReplaySummary, EvalDataset, DatasetItem, DatasetItemWithInput, DatasetItemListResponse, IdealAssessment, EvaluationCriteria, DatasetCategory, EvalRun, EvalRunListResponse, PerformanceSummary, AnswerSecondReadResult } from '../types';
import { getErrorMessage } from '../types';

/** One row of the Related dialog. Mirrors `RelatedOperationRow` in
 *  `functions/src/admin/analytics/getRelatedOperations.ts`. */
/** One row of the model run Related dialog. Mirrors `RelatedModelRunRow` in
 *  `functions/src/admin/monitoring/modelRunReplay.ts`. */
export interface RelatedModelRunRow {
  key: string;
  operation: string;
  at?: string;
  reason: string;
  detail?: string;
  href: string;
  isCurrent?: boolean;
  group: 'this-operation' | 'same-submission' | 'replays';
}

export interface RelatedOperationRow {
  key: string;
  operation: string;
  at?: string;
  /** Why the row is in the list — the relationship. */
  reason: string;
  /** What happened — score, status, note, who asked. */
  detail?: string;
  href?: string;
  isCurrent?: boolean;
  group: 'this-attempt' | 'other-attempts' | 'same-sitting';
}
import type { PromptInfo, PromptVersionInfo, Targeting, PromptType, OutputSchemaType } from '../types/settings';
import type {
  ScenarioInput, ScenarioInputsList, ScenarioEvalRun, ScenarioRunProgress,
  ScenarioRunStatistics, ComparisonReport, CaptureResult, ScenarioLabel,
  ScenarioEvalRunItemResult, JudgeRunsList,
  BenchMatrix, CandidateSubmission, EvalVerdict, EvalRowRanking, VerdictValue, VariantQuality, ResolvedBench,
  Scorecard, AgreementReport, JudgeBenchResult,
} from '../types/scenarioEvals';
import type {
  AssignmentKey, AssignmentListResult, AssignmentMatrix, AssignmentAnalyses, StudentAnalysesResult,
  PromoteAssignmentResult, JudgeAssignmentResult, AnalysisStepVerdict,
  AssignmentBenchRowResult,
} from '../types/assignmentQuality';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';
import { waitForAsyncJob, type PolledJobView } from '@shared-ui/api';

/** What a re-detect run produced. `clearedEncounters` is how many stale
 *  encounters this submission had before the re-run wiped them. */
export interface DetectLearningMomentsResult {
  submissionId: string;
  userId: string;
  questionId: string;
  clearedEncounters: number;
  detected: Array<{ learningMomentId: string; momentName: string; wasPositive: boolean; context: string }>;
}

export const modelApi = {
  // Prompts
  getPrompts: (params?: { includeTemplate?: boolean; type?: PromptType; includeArchived?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.includeTemplate) searchParams.set('includeTemplate', 'true');
    if (params?.type) searchParams.set('type', params.type);
    if (params?.includeArchived) searchParams.set('includeArchived', 'true');
    const query = searchParams.toString();
    return fetchWithAdmin<PromptInfo[]>(`/console/prompts${query ? `?${query}` : ''}`);
  },

  savePrompt: async (prompt: { id?: string; name: string; description: string; userPrompt: string; systemPrompt?: string; type?: PromptType; targeting?: Targeting; assessmentField?: string; assessmentSourceType?: string; jsonSchema?: { name: string; schema: object }; useStrictJsonSchema?: boolean; outputSchemaType?: OutputSchemaType }): Promise<PromptInfo> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/prompts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prompt),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<PromptInfo> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as PromptInfo;
  },

  deletePrompt: async (promptId: string): Promise<{ deleted: string; versionsDeleted: number; submissionsDeleted: number; experimentsDeleted: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/prompts/${promptId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    // Always try to parse the response body to get error details
    const data: ApiResponse<{ deleted: string; versionsDeleted: number; submissionsDeleted: number; experimentsDeleted: number }> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as { deleted: string; versionsDeleted: number; submissionsDeleted: number; experimentsDeleted: number };
  },

  deletePromptVersion: async (promptId: string, version: number): Promise<{ deleted: string; promptDeleted: boolean; submissionsDeleted: number; experimentsDeleted: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/prompts/${promptId}/version/${version}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    // Always try to parse the response body to get error details
    const data: ApiResponse<{ deleted: string; promptDeleted: boolean; submissionsDeleted: number; experimentsDeleted: number }> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as { deleted: string; promptDeleted: boolean; submissionsDeleted: number; experimentsDeleted: number };
  },

  getPromptHistory: (promptId: string) =>
    fetchWithAdmin<PromptVersionInfo[]>(`/console/prompts/${promptId}/history`),

  /** Phase 5 "Promote to git" — fetches the active version of a prompt as a
   *  serialized .prompt.md file. Caller turns the returned content into a
   *  browser download. See docs/FeatureDesigns/PipelineInfra/prompt-versioning.md. */
  promotePromptToFile: (promptId: string) =>
    fetchWithAdmin<{
      filename: string;
      content: string;
      promptId: string;
      versionDocId: string;
      version: number;
      source: 'git' | 'admin';
      activatedAt: string;
      activatedBy: string;
      reason: string;
    }>(`/console/prompts/${promptId}/promote-file`),

  getCanonicalSchemas: () =>
    fetchWithAdmin<Partial<Record<PromptType, { name: string; schema: object }>>>(`/console/prompts/canonical-schemas`),

  importPrompts: async (prompts: PromptInfo[]): Promise<{ imported: number; created: number; updated: number }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/prompts/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompts }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<{ imported: number; created: number; updated: number }> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as { imported: number; created: number; updated: number };
  },

  // Model runs for a submission. Returns slimmed ModelOutput[] (no
  // rawOutputFull/rawOutput/promptText/systemPromptText — those are
  // fetched separately by ModelRunDetail when a row is selected).
  getModelRuns: (submissionId: string) =>
    fetchWithAdmin<ModelOutput[]>(`/console/solution-analyses/${submissionId}/runs`),

  // Everything else the platform recorded near this attempt — its reruns and
  // re-evaluations, its split, its learning moments, the same question in other
  // sittings, the rest of the paper.
  getRelatedOperations: (submissionId: string) =>
    fetchWithAdmin<RelatedOperationRow[]>(`/console/solution-analyses/${submissionId}/related`),

  saveManualEvaluation: async (
    runId: string,
    submissionId: string,
    evaluation: Omit<ManualEvaluation, 'evaluatedAt'>
  ): Promise<ModelOutput> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/model-runs/${runId}/evaluate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ submissionId, ...evaluation }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data: ApiResponse<ModelOutput> = await response.json();

    if (!data.success) {
      throw new Error(getErrorMessage(data.error) || 'Unknown error');
    }

    return data.data as ModelOutput;
  },

  rerunSubmission: async (submissionId: string, promptType?: string, runs?: { promptId: string; provider: string; isPrimary?: boolean }[], temperature?: number, maxTokens?: number, pipelineConfig?: string, snapshotMode?: 'latest' | 'original'): Promise<{ newSubmissionId: string; originalSubmissionId: string; success: boolean }> => {
    const token = authApi.getToken();

    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/console/solution-analyses/${submissionId}/rerun`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(runs ? { runs, temperature, maxTokens, snapshotMode } : { promptType, pipelineConfig, snapshotMode }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ newSubmissionId: string; originalSubmissionId: string; success: boolean }> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as { newSubmissionId: string; originalSubmissionId: string; success: boolean };
  },

  /** Re-run learning-moment detection on one already-analysed submission.
   *  Replaces this submission's prior encounters rather than adding to them,
   *  so an author can iterate on a `detectionHint` without inflating counts. */
  detectLearningMoments: async (submissionId: string): Promise<DetectLearningMomentsResult> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/solution-analyses/${submissionId}/detect-learning-moments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<DetectLearningMomentsResult> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as DetectLearningMomentsResult;
  },

  /** Run the answer second read on one analysis: the final answer only, read
   *  independently off the uncropped page. Observation only — no score changes. */
  secondRead: async (submissionId: string): Promise<AnswerSecondReadResult> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/solution-analyses/${submissionId}/second-read`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<AnswerSecondReadResult> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status} ${response.statusText}`);
    }

    return data.data as AnswerSecondReadResult;
  },

  // Model Data (Eval System)
  getModelInputs: (params?: {
    page?: number;
    pageSize?: number;
    userId?: string;
    questionId?: string;
    source?: 'production' | 'eval' | 'replay';
    includeOutputs?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.questionId) searchParams.set('questionId', params.questionId);
    if (params?.source) searchParams.set('source', params.source);
    if (params?.includeOutputs) searchParams.set('includeOutputs', 'true');

    const query = searchParams.toString();
    return fetchWithAdmin<ModelDataResponse>(
      `/console/model/inputs${query ? `?${query}` : ''}`
    );
  },

  getModelInput: (id: string) => fetchWithAdmin<ModelInputWithOutputs>(`/console/model/inputs/${id}`),

  getModelStats: () => fetchWithAdmin<ModelStats>('/console/model/stats'),

  // Model Outputs (output-centric queries)
  getModelOutputs: (params?: {
    page?: number;
    pageSize?: number;
    source?: string;
    promptType?: string;
    subject?: string;
    userId?: string;
    /** One operation: the runs made for this submission or job. */
    submissionId?: string;
    email?: string;
    model?: string;
    sinceDate?: string;
    /** Only runs that recorded a per-call failure (errorRecords). Server-side,
     *  so `total` and paging describe the failures, not one page of them. */
    failedOnly?: boolean;
    sortBy?: 'generatedAt' | 'latencyMs' | 'cost';
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
    if (params?.source) searchParams.set('source', params.source);
    if (params?.promptType) searchParams.set('promptType', params.promptType);
    if (params?.subject) searchParams.set('subject', params.subject);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.submissionId) searchParams.set('submissionId', params.submissionId);
    if (params?.email) searchParams.set('email', params.email);
    if (params?.model) searchParams.set('model', params.model);
    if (params?.sinceDate) searchParams.set('sinceDate', params.sinceDate);
    if (params?.failedOnly) searchParams.set('failedOnly', 'true');
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    return fetchWithAdmin<ModelOutputsResponse>(
      `/console/model/outputs${query ? `?${query}` : ''}`
    );
  },

  getModelOutputById: (id: string) => fetchWithAdmin<ModelOutputWithInput>(`/console/model/outputs/${id}`),

  /** Re-send a recorded run's call to another model. Spends money; the result
   *  is for learning only (model-run-replay-target.md). Runs as a background
   *  job (a slow model outlasts an HTTP request): queue it, then wait for the
   *  job with the shared `waitForAsyncJob`, as `reEvaluate` does. The job is the
   *  polled on the console route: the user route admits only students and
   *  teachers, and an operator may be neither. */
  replayModelRun: async (id: string, body: { provider: string; maxTokens?: number; comment?: string }) => {
    const { jobId } = await fetchWithAdmin<{ jobId: string }>(`/console/model/outputs/${id}/replay`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return waitForAsyncJob<{ replayId: string }>(
      jobId_ => fetchWithAdmin<{ job: PolledJobView | null }>(`/console/async-jobs/${encodeURIComponent(jobId_)}`),
      jobId,
      // One attempt with a 9-minute timeout, plus the dispatcher's pickup.
      { maxWaitMs: 12 * 60_000 },
    );
  },

  /** The model runs beside this one: other steps of its pipeline run, other
   *  runs on the same submission or job, and its re-runs. */
  getRelatedModelRuns: (id: string) =>
    fetchWithAdmin<RelatedModelRunRow[]>(`/console/model/outputs/${id}/related`),

  /** Replays of a run (or of the run a replay was made from), and why the run
   *  cannot be replayed when it cannot. */
  getModelRunReplays: (id: string) =>
    fetchWithAdmin<{ replays: ModelRunReplaySummary[]; blocker?: string }>(`/console/model/outputs/${id}/replays`),

  // ============================================
  // Eval System APIs
  // ============================================

  // Datasets
  getEvalDatasets: (category?: DatasetCategory) => {
    const searchParams = new URLSearchParams();
    if (category) searchParams.set('category', category);
    const query = searchParams.toString();
    return fetchWithAdmin<{ datasets: EvalDataset[]; total: number }>(
      `/eval/datasets${query ? `?${query}` : ''}`
    );
  },

  getEvalDataset: (id: string) => fetchWithAdmin<EvalDataset>(`/eval/datasets/${id}`),

  createEvalDataset: async (dataset: {
    readableId: string;
    name: string;
    description: string;
    category: DatasetCategory;
  }): Promise<EvalDataset> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataset),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<EvalDataset> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as EvalDataset;
  },

  updateEvalDataset: async (id: string, dataset: {
    name?: string;
    description?: string;
    category?: DatasetCategory;
  }): Promise<EvalDataset> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataset),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<EvalDataset> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as EvalDataset;
  },

  deleteEvalDataset: async (id: string): Promise<{ deleted: boolean; labelsDeleted: number; inputsDeleted: number }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: boolean; labelsDeleted: number; inputsDeleted: number }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { deleted: boolean; labelsDeleted: number; inputsDeleted: number };
  },

  getEvalCategories: () => fetchWithAdmin<{ id: string; description: string }[]>('/eval/categories'),

  // Dataset Items
  getDatasetItems: (datasetId: string, params?: { pending?: boolean; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.pending) searchParams.set('pending', 'true');
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchWithAdmin<DatasetItemListResponse>(
      `/eval/datasets/${datasetId}/items${query ? `?${query}` : ''}`
    );
  },

  getDatasetItem: (datasetId: string, itemId: string) =>
    fetchWithAdmin<DatasetItemWithInput>(`/eval/datasets/${datasetId}/items/${itemId}`),

  deleteDatasetItem: async (datasetId: string, itemId: string): Promise<{ deleted: boolean }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets/${datasetId}/items/${itemId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: boolean }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { deleted: boolean };
  },

  importDatasetItems: async (
    datasetId: string,
    options: { inputIds?: string[]; submissionIds?: string[]; addedBy?: string }
  ): Promise<{ imported: number; skipped: number; failed: number; errors?: string[] }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets/${datasetId}/items/import`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ imported: number; skipped: number; failed: number; errors?: string[] }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { imported: number; skipped: number; failed: number; errors?: string[] };
  },

  labelDatasetItem: async (datasetId: string, itemId: string, label: {
    idealAssessment: IdealAssessment;
    evaluationCriteria?: EvaluationCriteria;
    labeledBy: string;
    confidence: 'high' | 'medium' | 'low';
    notes?: string;
  }): Promise<DatasetItem> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/datasets/${datasetId}/items/${itemId}/label`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(label),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<DatasetItem> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as DatasetItem;
  },

  // Eval Runs
  getEvalRuns: (params?: { datasetId?: string; status?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.datasetId) searchParams.set('datasetId', params.datasetId);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const query = searchParams.toString();
    return fetchWithAdmin<EvalRunListResponse>(
      `/eval/runs${query ? `?${query}` : ''}`
    );
  },

  getEvalRun: (runId: string) => fetchWithAdmin<EvalRun>(`/eval/runs/${runId}`),

  getEvalPerformanceSummary: () => fetchWithAdmin<PerformanceSummary>('/eval/performance'),

  createEvalRun: async (run: {
    datasetId: string;
    readableId?: string;
    parameters: {
      promptId: string;
      promptVersion?: number;
      provider: string;
      model: string;
      temperature?: number;
    };
  }): Promise<EvalRun> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/runs`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(run),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<EvalRun> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as EvalRun;
  },

  deleteEvalRun: async (runId: string): Promise<{ deleted: boolean }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/eval/runs/${runId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: boolean }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { deleted: boolean };
  },

  // ============================================
  // Scenario-level evals
  // ============================================

  // Scenario inputs (dataset builder + labeling)
  captureScenarioInputs: (datasetId: string, body: { inputIds?: string[]; submissionIds?: string[]; variantQuality?: VariantQuality; difficultyTags?: string[]; curationNote?: string }) =>
    fetchWithAdmin<CaptureResult>(`/eval/datasets/${datasetId}/scenario-inputs/capture`, { method: 'POST', body: JSON.stringify(body) }),

  listScenarioInputs: (datasetId: string, unlabeledOnly?: boolean) =>
    fetchWithAdmin<ScenarioInputsList>(`/eval/datasets/${datasetId}/scenario-inputs${unlabeledOnly ? '?unlabeled=true' : ''}`),

  getScenarioInput: (datasetId: string, id: string) =>
    fetchWithAdmin<ScenarioInput>(`/eval/datasets/${datasetId}/scenario-inputs/${id}`),

  editScenarioInput: (datasetId: string, id: string, body: Partial<Pick<ScenarioInput, 'question' | 'studentProfile' | 'studentSolution' | 'variantQuality' | 'difficultyTags' | 'curationNote'>>) =>
    fetchWithAdmin<ScenarioInput>(`/eval/datasets/${datasetId}/scenario-inputs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  labelScenarioInput: (datasetId: string, id: string, body: Omit<ScenarioLabel, 'labeledAt' | 'labeledBy'>) =>
    fetchWithAdmin<ScenarioInput>(`/eval/datasets/${datasetId}/scenario-inputs/${id}/label`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteScenarioInput: (datasetId: string, id: string) =>
    fetchWithAdmin<{ deleted: boolean }>(`/eval/datasets/${datasetId}/scenario-inputs/${id}`, { method: 'DELETE' }),

  // ── Assessment eval bench ──────────────────────────────────────────
  // The unit is a CELL: one prompt execution at one pipeline step, so
  // extraction and analysis are judged separately.
  // See docs/FeatureDesigns/Evals/assessment-eval-bench.md.

  // Benches are declared in code, not created here — this resolves them
  // (materializing on first call). See Backend/functions/src/eval/benchRegistry.ts.
  listBenches: () =>
    fetchWithAdmin<{ benches: ResolvedBench[] }>(`/eval/benches`),

  listBenchCandidates: (datasetId: string, questionId: string) =>
    fetchWithAdmin<{ candidates: CandidateSubmission[] }>(
      `/eval/datasets/${datasetId}/candidate-submissions?questionId=${encodeURIComponent(questionId)}`),

  // `rowId` narrows to one row — an assignment dataset carries a row per
  // submission, and a drawer opened on one of them has no use for the rest.
  getBenchMatrix: (datasetId: string, includeHistorical = true, rowId?: string) => {
    const qs = new URLSearchParams();
    if (!includeHistorical) qs.set('historical', 'false');
    if (rowId) qs.set('rowId', rowId);
    const query = qs.toString();
    return fetchWithAdmin<BenchMatrix>(
      `/eval/datasets/${datasetId}/matrix${query ? `?${query}` : ''}`);
  },

  putBenchVerdict: (datasetId: string, rowId: string, outputId: string, body: {
    verdict: VerdictValue; conditionalVerdict?: VerdictValue; failureTags?: string[];
    critique?: string; upstreamFailure?: boolean; runId?: string;
  }) =>
    fetchWithAdmin<EvalVerdict>(`/eval/datasets/${datasetId}/verdicts/${rowId}/${outputId}`,
      { method: 'PUT', body: JSON.stringify(body) }),

  deleteBenchVerdict: (datasetId: string, rowId: string, outputId: string) =>
    fetchWithAdmin<{ deleted: boolean }>(`/eval/datasets/${datasetId}/verdicts/${rowId}/${outputId}`,
      { method: 'DELETE' }),

  putBenchRanking: (datasetId: string, rowId: string, body: { ranking: string[]; note?: string }) =>
    fetchWithAdmin<EvalRowRanking>(`/eval/datasets/${datasetId}/rankings/${rowId}`,
      { method: 'PUT', body: JSON.stringify(body) }),

  // Judge cells that have no judge verdict yet. Idempotent unless `force`.
  judgeBench: (datasetId: string, body: { rowSubset?: string[]; force?: boolean; includeHistorical?: boolean } = {}) =>
    fetchWithAdmin<JudgeBenchResult>(`/eval/datasets/${datasetId}/judge`,
      { method: 'POST', body: JSON.stringify(body) }),

  getBenchAgreement: (datasetId: string) =>
    fetchWithAdmin<AgreementReport>(`/eval/datasets/${datasetId}/agreement`),

  getBenchScorecard: (datasetId: string, evaluator: 'human' | 'judge' = 'human') =>
    fetchWithAdmin<Scorecard>(`/eval/datasets/${datasetId}/scorecard?evaluator=${evaluator}`),

  getBenchBundle: (datasetId: string) =>
    fetchWithAdmin<{ lines: number; jsonl: string }>(`/eval/datasets/${datasetId}/bundle`),

  // Scenario runs
  startScenarioRun: (body: { datasetId: string; pipelineConfigName: string; itemSubset?: string[]; readableId?: string }) =>
    fetchWithAdmin<ScenarioEvalRun>(`/eval/scenario-runs`, { method: 'POST', body: JSON.stringify(body) }),

  getScenarioRun: (runId: string) =>
    fetchWithAdmin<ScenarioRunProgress>(`/eval/scenario-runs/${runId}`),

  getScenarioRunItems: (runId: string) =>
    fetchWithAdmin<{ items: ScenarioEvalRunItemResult[] }>(`/eval/scenario-runs/${runId}/items`),

  scoreScenarioRun: (runId: string, useJudge = false, forceJudge = false) =>
    fetchWithAdmin<ScenarioRunStatistics>(`/eval/scenario-runs/${runId}/score`, {
      method: 'POST',
      body: JSON.stringify({ useJudge, forceJudge }),
    }),

  getJudgeRuns: (params: { page: number; pageSize: number }) =>
    fetchWithAdmin<JudgeRunsList>(`/eval/judge-runs?page=${params.page}&pageSize=${params.pageSize}`),

  listScenarioRuns: (datasetId: string) =>
    fetchWithAdmin<{ runs: ScenarioEvalRun[] }>(`/eval/datasets/${datasetId}/scenario-runs`),

  setScenarioRunBaseline: (runId: string) =>
    fetchWithAdmin<ScenarioEvalRun>(`/eval/scenario-runs/${runId}/baseline`, { method: 'PUT' }),

  compareScenarioRuns: (baselineRunId: string, candidateRunId: string) =>
    fetchWithAdmin<ComparisonReport>(`/eval/scenario-comparisons?baselineRunId=${baselineRunId}&candidateRunId=${candidateRunId}`),

  // Assignment analysis quality — see docs/FeatureDesigns/Evals/assignment-analysis-quality.md
  listEvalAssignments: (params: { from?: string; to?: string; assignerId?: string; search?: string } = {}) =>
    fetchWithAdmin<AssignmentListResult>(`/eval/assignments${assignmentQuery(params)}`),

  getAssignmentMatrix: (key: AssignmentKey) =>
    fetchWithAdmin<AssignmentMatrix>(
      `/eval/assignments/${encodeURIComponent(key.questionSetId)}/matrix${assignmentKeyQuery(key)}`),

  getAssignmentAnalyses: (key: AssignmentKey) =>
    fetchWithAdmin<AssignmentAnalyses>(
      `/eval/assignments/${encodeURIComponent(key.questionSetId)}/analyses${assignmentKeyQuery(key)}`),

  listStudentAnalyses: (studentId: string, params: { limit?: number; offset?: number; questionSetId?: string; subject?: string } = {}) =>
    fetchWithAdmin<StudentAnalysesResult>(
      `/eval/students/${encodeURIComponent(studentId)}/analyses${assignmentQuery(params)}`),

  getAnalysisVerdicts: (submissionId: string) =>
    fetchWithAdmin<{ verdicts: AnalysisStepVerdict[] }>(
      `/eval/analyses/${encodeURIComponent(submissionId)}/verdicts`),

  // Writes: promote captures rows into an eval dataset; judge additionally
  // writes verdicts for what the assignment actually shipped.
  promoteAssignment: (key: AssignmentKey) =>
    fetchWithAdmin<PromoteAssignmentResult>(
      `/eval/assignments/${encodeURIComponent(key.questionSetId)}/promote${assignmentKeyQuery(key)}`,
      { method: 'POST' }),

  judgeAssignment: (key: AssignmentKey, force = false) =>
    fetchWithAdmin<JudgeAssignmentResult>(
      `/eval/assignments/${encodeURIComponent(key.questionSetId)}/judge${assignmentKeyQuery(key)}`,
      { method: 'POST', body: JSON.stringify({ force }) }),

  // Capture ONE analysis as a bench row so its steps can be judged. Not
  // `promoteAssignment`, which would take the other 173 rows with it — see
  // docs/FeatureDesigns/Evals/assignment-step-verdicts.md §2.1.
  ensureAssignmentBenchRow: (key: AssignmentKey, submissionId: string) =>
    fetchWithAdmin<AssignmentBenchRowResult>(
      `/eval/assignments/${encodeURIComponent(key.questionSetId)}/bench-row${assignmentKeyQuery(key)}`,
      { method: 'POST', body: JSON.stringify({ submissionId }) }),
};

/** Build a query string from the defined entries only — an empty filter must
 *  not become `?subject=undefined`. */
function assignmentQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** The assignment key as query params: `by` (assigner) and optional `batchId`. */
function assignmentKeyQuery(key: AssignmentKey): string {
  return assignmentQuery({ by: key.assignedById, batchId: key.batchId });
}
